import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'

export interface BreadcrumbItem {
  label: string
  to?: string
}

interface BreadcrumbProps {
  items: BreadcrumbItem[]
}

/**
 * Nested navigator (breadcrumb) for page hierarchy. Use on every page so users can navigate back.
 * Last item is current page (no link). Earlier items link to parent routes when `to` is set.
 */
export function Breadcrumb({ items }: BreadcrumbProps): React.ReactElement {
  if (items.length === 0) return <></>

  return (
    <nav aria-label="Breadcrumb" className="py-1.5 text-sm leading-snug">
      <ol className="flex flex-wrap items-center gap-1.5 text-gray-500 dark:text-gray-400">
        {items.map((item, index) => {
          const isLast = index === items.length - 1
          return (
            <li key={index} className="flex items-center gap-1.5">
              {index > 0 && (
                <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
              )}
              {isLast || !item.to ? (
                <span className="font-medium text-gray-800 dark:text-gray-100">
                  {item.label}
                </span>
              ) : (
                <Link
                  to={item.to}
                  className="transition-colors hover:text-gray-800 dark:hover:text-gray-200"
                >
                  {item.label}
                </Link>
              )}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

Breadcrumb.displayName = 'Breadcrumb'
